import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../contexts/LanguageContext';

const BoardSizeSlider = ({ boardSize, setBoardSize }) => {
  const { t } = useTranslation();
  const [previousSize, setPreviousSize] = useState(boardSize);
  const [sizeDirection, setSizeDirection] = useState(null);
  const timeoutRef = useRef(null);
  
  // Track size changes for visual feedback
  useEffect(() => {
    if (boardSize !== previousSize) {
      setSizeDirection(boardSize > previousSize ? 'up' : 'down');
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      
      timeoutRef.current = setTimeout(() => {
        setSizeDirection(null);
        setPreviousSize(boardSize);
      }, 500);
    }
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [boardSize, previousSize]);

  const sizeDiff = boardSize - 100;
  
  return (
    <section className="settings-section board-size-section">
      <h3>📐 {t('settings.boardSize', 'Board Size')}</h3>
      <p className="section-description">{t('settings.boardSizeDescription', 'Adjust the chessboard size to your preference')}</p>
      
      <div className="board-size-slider-container">
        <div className="board-size-header">
          <div className="board-size-labels">
            <span>{t('settings.smaller', 'Smaller')}</span>
            <span className={`board-size-value ${sizeDirection ? `size-${sizeDirection}` : ''}`}>{boardSize}%</span>
            <span>{t('settings.larger', 'Larger')}</span>
          </div>
          {/* Size comparison indicator */}
          <div className={`board-size-comparison ${sizeDiff !== 0 ? 'visible' : ''}`}>
            {sizeDiff > 0 ? (
              <span className="board-indicator larger">+{sizeDiff}%</span>
            ) : sizeDiff < 0 ? (
              <span className="board-indicator smaller">{sizeDiff}%</span>
            ) : null}
          </div>
        </div>
        <div className="board-slider-with-markers">
          <input
            type="range"
            min="70"
            max="130"
            step="5"
            value={boardSize}
            onChange={(e) => {
              const newSize = parseInt(e.target.value);
              setBoardSize(newSize);
              // Apply immediately for real-time preview
              document.documentElement.style.setProperty('--board-size-scale', newSize / 100);
            }}
            className="board-size-slider"
            data-testid="board-size-slider"
          />
          {/* Tick markers */}
          <div className="board-slider-ticks">
            <span className="tick" style={{left: '0%'}} title="70%"></span>
            <span className="tick default-tick" style={{left: '50%'}} title="100% (Default)"></span>
            <span className="tick" style={{left: '100%'}} title="130%"></span>
          </div>
        </div>
        <div className="board-size-presets">
          <button 
            className={`size-preset-btn ${boardSize === 85 ? 'active' : ''}`}
            onClick={() => {
              setBoardSize(85);
              document.documentElement.style.setProperty('--board-size-scale', 0.85);
            }}
            data-testid="board-size-compact"
          >
            {t('settings.compact', 'Compact')}
          </button>
          <button 
            className={`size-preset-btn ${boardSize === 100 ? 'active' : ''}`}
            onClick={() => {
              setBoardSize(100);
              document.documentElement.style.setProperty('--board-size-scale', 1);
            }}
            data-testid="board-size-default"
          >
            {t('settings.default', 'Default')}
          </button>
          <button 
            className={`size-preset-btn ${boardSize === 115 ? 'active' : ''}`}
            onClick={() => {
              setBoardSize(115);
              document.documentElement.style.setProperty('--board-size-scale', 1.15);
            }}
            data-testid="board-size-large"
          >
            {t('settings.large', 'Large')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default BoardSizeSlider;
